import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SupportChatHelpButton } from '@/components/common';
import { Button, Label } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { AuthContext, ProfilePictureContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { formatDate } from '@/helpers/date';
import { useCanUseSupportChat, useIsUploaderSidebarShown, useMobileScreen } from '@/hooks';
import { SIDEBAR_LINK_BUTTON_STYLE } from '@/layout/Sidebar/Sidebar.styles';
import { UserAvatar } from '@/pages/ProfilePage/UserAvatar';
import { LeftChevronIcon as ArrowLeftIcon, LogOut as LogOutIcon, SentoaLogo } from '@/resources';
import { TestIds } from '@/shared/test-ids';

type UploadContentLayoutProps = {
  goBack?: () => void;
  children: React.ReactNode;
};

export const UploadContentLayout = ({ children, goBack }: UploadContentLayoutProps) => {
  const [t] = useTranslation('common');
  const { logout, permissions, currentUserData } = useContext(AuthContext);
  const isMobile = useMobileScreen();
  const canUseSupportChat = useCanUseSupportChat();

  const navigate = useNavigate();

  const { profilePictureUrl } = useContext(ProfilePictureContext);

  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const isSidebarShown = useIsUploaderSidebarShown();

  return (
    <div className='flex h-screen w-full flex-col'>
      {(!hasOrgAccess || isMobile) && !isSidebarShown && (
        <div className='relative top-0 z-20 flex h-[45px] w-full items-center bg-primary-gray'>
          {goBack && (
            <Button
              variant={ButtonVariant.SECONDARY}
              handleOnClick={goBack}
              className='absolute left-0 border-0 !px-3'>
              <div>
                <ArrowLeftIcon className='text-base text-primary-blue' />
              </div>
            </Button>
          )}
          <div className='flex-1'></div>
          <SentoaLogo className='absolute left-1/2 h-[26px] w-[108px] -translate-x-1/2' />
          <div className='ml-auto flex items-center'>
            {canUseSupportChat && <SupportChatHelpButton className='mr-4 md:hidden' />}
            <div
              className={`mr-0 hidden md:mr-2 md:flex ${SIDEBAR_LINK_BUTTON_STYLE} ${window.location.pathname.startsWith('/profile') ? 'border-r-4 border-r-primary-blue !font-bold !text-primary-blue' : ''}`}
              onClick={() => navigate('/profile')}>
              {profilePictureUrl ? (
                <img
                  src={profilePictureUrl}
                  alt={t('profilePicture')}
                  className='mr-1 size-7 rounded-full object-cover'
                />
              ) : (
                <UserAvatar style='w-7 h-7 text-sm bg-primary-white border border-accent-light-gray text-primary-blue' />
              )}
              <div className='flex flex-col'>
                <div className='ml-1 max-w-[140px] overflow-hidden truncate'>
                  {currentUserData?.firstName + ' ' + currentUserData?.lastName}
                </div>
                {currentUserData?.role && (
                  <div className='ml-1 text-xs !font-normal !text-secondary-steel-blue'>
                    {t(currentUserData?.role.name)}
                  </div>
                )}
              </div>
            </div>
            <div
              className='mr-4 block cursor-pointer md:hidden'
              onClick={() => navigate('/profile')}>
              {profilePictureUrl ? (
                <img
                  src={profilePictureUrl}
                  alt={t('profilePicture')}
                  className='size-7 rounded-full object-cover'
                />
              ) : (
                <UserAvatar style='w-7 h-7 text-sm bg-primary-white border border-accent-light-gray text-primary-blue' />
              )}
            </div>
            <span data-testid={TestIds.layout.logoutButton} className='mr-3 flex'>
              <LogOutIcon className='cursor-pointer text-2xl text-primary-blue' onClick={logout} />
            </span>
          </div>
        </div>
      )}
      <div className='flex size-full grow overflow-y-auto pt-4'>
        <div className='flex size-full flex-col justify-between'>
          <div className='flex w-full flex-col items-center px-3'>{children}</div>
          <footer className='mt-4 flex !h-[18px] w-full justify-center bg-secondary-steel-blue md:hidden'>
            <Label
              text={`${t('copyrights')} © ${t('sentoa')} ${formatDate(new Date(), { year: 'numeric' })}`}
              className='py-[3px] font-sans !text-2xs !font-normal !text-primary-white'
            />
          </footer>
        </div>
      </div>
    </div>
  );
};
